import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Send,
  Search,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";
import Logo from "./Logo";
import { NAV_CATEGORIES, COMUNICAZIONI_SUB, TODAY_LABEL } from "../mock";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [comOpen, setComOpen] = useState(false);
  const [bilOpen, setBilOpen] = useState(false);
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/cerca?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
      setQuery("");
    }
  };

  const socials = [Facebook, Twitter, Instagram, Linkedin, Send];

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      {/* Top bar */}
      <div className="border-b border-sq">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 grid grid-cols-3 items-center">
          <div className="flex items-center gap-2">
            {socials.map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="text-gray-400 hover:text-sq-orange transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
          <div className="flex justify-center">
            <Logo />
          </div>
          <div className="hidden md:block text-right text-sm text-gray-500">
            {TODAY_LABEL}
          </div>
          <button
            className="md:hidden justify-self-end text-sq-navy"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Nav bar */}
      <div className="border-b border-sq hidden md:block">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          <nav className="flex items-center">
            <Link
              to="/"
              className="py-4 px-3 text-[15px] font-medium text-sq-navy border-b-2 border-sq-orange"
            >
              Home
            </Link>
            {NAV_CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/categoria/${c.slug}`}
                className="py-4 px-3 text-[15px] font-medium text-sq-navy border-b-2 border-transparent hover:border-sq-orange hover:text-sq-orange transition-colors"
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/societa"
              className="py-4 px-3 text-[15px] font-medium text-sq-navy border-b-2 border-transparent hover:border-sq-orange hover:text-sq-orange transition-colors"
            >
              Schede società
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setBilOpen(true)}
              onMouseLeave={() => setBilOpen(false)}
            >
              <Link
                to="/bilancio-60-secondi"
                className="py-4 px-3 text-[15px] font-medium text-sq-navy border-b-2 border-transparent hover:border-sq-orange hover:text-sq-orange transition-colors inline-flex items-center gap-1"
              >
                Bilancio in 60 secondi
                <ChevronDown size={14} />
              </Link>
              {bilOpen && (
                <div className="absolute left-0 top-full bg-white shadow-xl border border-sq rounded-sm w-64 py-2 z-50">
                  <Link to="/bilancio-60-secondi" className="block px-4 py-2.5 text-sm text-sq-navy hover:bg-sq-light hover:text-sq-orange transition-colors">
                    Schede EGM
                  </Link>
                  <Link to="/trasparenza-egm" className="block px-4 py-2.5 text-sm text-sq-navy hover:bg-sq-light hover:text-sq-orange transition-colors">
                    🔍 Classifica Trasparenza EGM
                  </Link>
                </div>
              )}
            </div>
            {/* Comunicazioni dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setComOpen(true)}
              onMouseLeave={() => setComOpen(false)}
            >
              <Link
                to="/comunicazioni"
                className="py-4 px-3 text-[15px] font-medium text-sq-navy border-b-2 border-transparent hover:border-sq-orange hover:text-sq-orange transition-colors inline-flex items-center gap-1"
              >
                Comunicazioni societarie
                <ChevronDown size={14} />
              </Link>
              {comOpen && (
                <div className="absolute left-0 top-full bg-white shadow-xl border border-sq rounded-sm w-72 py-2 z-50">
                  {COMUNICAZIONI_SUB.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/comunicazioni/${s.slug}`}
                      className="block px-4 py-2.5 text-sm text-sq-navy hover:bg-sq-light hover:text-sq-orange transition-colors"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>
          <div className="relative">
            <button
              onClick={() => setSearchOpen((o) => !o)}
              className="text-sq-navy hover:text-sq-orange transition-colors"
            >
              <Search size={20} />
            </button>
            {searchOpen && (
              <form
                onSubmit={onSearch}
                className="absolute right-0 top-full mt-2 bg-white shadow-xl border border-sq rounded-sm p-2 flex w-72 z-50"
              >
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca notizie..."
                  className="flex-1 px-3 py-2 text-sm outline-none text-sq-navy"
                />
                <button
                  type="submit"
                  className="bg-sq-orange hover:bg-sq-orange-dark text-white px-3 rounded-sm text-sm transition-colors"
                >
                  Cerca
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-72 bg-white shadow-2xl p-5 overflow-y-auto fade-in">
            <div className="flex justify-between items-center mb-6">
              <span className="font-serif-display text-xl text-sq-navy">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="text-sq-navy">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={onSearch} className="flex mb-5">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca..."
                className="flex-1 px-3 py-2 text-sm border border-sq rounded-l-sm outline-none"
              />
              <button className="bg-sq-orange text-white px-3 rounded-r-sm text-sm">
                <Search size={16} />
              </button>
            </form>
            <nav className="flex flex-col" onClick={() => setMobileOpen(false)}>
              <Link to="/" className="py-3 border-b border-sq text-sq-navy font-medium">
                Home
              </Link>
              {NAV_CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/categoria/${c.slug}`}
                  className="py-3 border-b border-sq text-sq-navy font-medium"
                >
                  {c.name}
                </Link>
              ))}
              <Link to="/societa" className="py-3 border-b border-sq text-sq-navy font-medium">
                Schede società quotate
              </Link>
              <Link to="/bilancio-60-secondi" className="py-3 border-b border-sq text-sq-navy font-medium">
                Bilancio in 60 secondi
              </Link>
              <Link to="/trasparenza-egm" className="py-2.5 pl-4 border-b border-sq text-sm text-gray-600">
                🔍 Classifica Trasparenza EGM
              </Link>
              <Link to="/comunicazioni" className="py-3 border-b border-sq text-sq-navy font-medium">
                Comunicazioni societarie
              </Link>
              {COMUNICAZIONI_SUB.map((s) => (
                <Link
                  key={s.slug}
                  to={`/comunicazioni/${s.slug}`}
                  className="py-2.5 pl-4 border-b border-sq text-sm text-gray-600"
                >
                  {s.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
